import React, { useRef, useEffect } from "react";
import DXFParser from "dxf-parser";
import { useColorMode } from "../../app/theme/ColorModeContextProvider.tsx";
import { Typography } from "@mui/material";

interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}


interface Props {
  content: string;
}

const DxfViewer: React.FC<Props> = ({ content }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const { mode } = useColorMode();

    const updateBounds = (x: number, y: number, bounds: Bounds) => {
        if (x < bounds.minX) {
            bounds.minX = x;
        }
        if (x > bounds.maxX) {
            bounds.maxX = x;
        }
        if (y < bounds.minY) {
            bounds.minY = y;
        }
        if (y > bounds.maxY) {
            bounds.maxY = y;
        }
    };

    const scaleDxf = (width: number, entities: any[]) => {
        const bounds: Bounds = {
            minX: Infinity,
            minY: Infinity,
            maxX: -Infinity,
            maxY: -Infinity,
        };

        for (const entity of entities) {
            if (entity.type === "LINE") {
                updateBounds(entity["vertices"][0].x, entity["vertices"][0].y, bounds);
                updateBounds(entity["vertices"][1].x, entity["vertices"][1].y, bounds);
            }
            else if (entity.type === "CIRCLE") {
                updateBounds(entity.center.x - entity.radius, entity.center.y - entity.radius, bounds);
                updateBounds(entity.center.x + entity.radius, entity.center.y + entity.radius, bounds);
            }
            else if (entity.type === "ARC") {
                const { center, radius, startAngle, endAngle } = entity;
                function normalize(angle: number) {
                    return (angle + 2 * Math.PI) % (2 * Math.PI);
                }

                const s = normalize(startAngle);
                const e = normalize(endAngle);

                const anglesToCheck: number[] = [s, e];
                const criticalAngles = [0, Math.PI / 2, Math.PI, 3 * Math.PI / 2];

                for (const a of criticalAngles) {
                    const aNorm = normalize(a);
                    const inArc =
                        s < e
                            ? aNorm >= s && aNorm <= e
                            : aNorm >= s || aNorm <= e;

                    if (inArc) {
                        anglesToCheck.push(aNorm);
                    }
                }

                for (const angle of anglesToCheck) {
                    const x = center.x + radius * Math.cos(angle);
                    const y = center.y + radius * Math.sin(angle);
                    updateBounds(x, y, bounds);
                }
            }
        }
        
        let moveX = bounds.minX < 0 ? bounds.minX * (-1) : 0;
        let moveY = bounds.minY < 0 ? bounds.minY * (-1) : 0;

        const scaleFactor = width / (bounds.maxX - bounds.minX);
        for (const entity of entities) {
            if (entity.type === "LINE") {
                entity["vertices"][0].x = (entity["vertices"][0].x + moveX) * scaleFactor;
                entity["vertices"][0].y = (entity["vertices"][0].y + moveY) * scaleFactor;
                entity["vertices"][1].x = (entity["vertices"][1].x + moveX) * scaleFactor;
                entity["vertices"][1].y = (entity["vertices"][1].y + moveY) * scaleFactor;
            }
            else if (entity.type === "CIRCLE") {
                entity.center.x = (entity.center.x + moveX) * scaleFactor;
                entity.center.y = (entity.center.y + moveY) * scaleFactor;
                entity.center.z *= scaleFactor;
                entity.radius *= scaleFactor;
            }
            else if (entity.type === "ARC") {
                entity.center.x = (entity.center.x + moveX) * scaleFactor;
                entity.center.y = (entity.center.y + moveY) * scaleFactor;
                if (entity.center.z !== undefined) {
                    entity.center.z *= scaleFactor;
                }
                entity.radius *= scaleFactor;
            }
        }
        const scaledHeight = (bounds.maxY - bounds.minY) * scaleFactor;
        return scaledHeight;
    }

    const draw = (entities: any[], canvas: any) => {
        if (!canvas) 
            return;
        const canvasContext = canvas.getContext("2d");
        if (!canvasContext) 
            return;

        canvasContext.clearRect(0, 0, canvas.width, canvas.height);
        if (mode === "light") {
            canvasContext.strokeStyle = "black";
        } else {
            canvasContext.strokeStyle = "white";
        }

        for (const entity of entities) {
            if (entity.type === "LINE") {
                canvasContext.beginPath();canvasContext.moveTo(entity["vertices"][0].x, canvas.height - entity["vertices"][0].y);
                canvasContext.lineTo(entity["vertices"][1].x, canvas.height - entity["vertices"][1].y);
                canvasContext.stroke();
            }  
            else if (entity.type === "CIRCLE") {
                canvasContext.beginPath();
                canvasContext.arc(entity.center.x, canvas.height - entity.center.y, entity.radius, 0, 2 * Math.PI);

                canvasContext.stroke();
            }
            else if (entity.type === "ARC") {
                canvasContext.beginPath();
                canvasContext.arc(entity.center.x, canvas.height - entity.center.y, entity.radius, entity.startAngle, entity.endAngle, false);
                canvasContext.stroke();
            }
        }
    }

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const resizeObserver = new ResizeObserver(() => {
            window.setTimeout(() => {
                if (!content) return;

                const parser = new DXFParser();
                const dxf = parser.parseSync(content);
                if (!dxf) return;

                const rect = canvas.getBoundingClientRect();
                const scaledHeight = scaleDxf(canvas.width, dxf.entities);
                canvas.width = rect.width;
                canvas.height = scaledHeight;
                draw(dxf.entities, canvas);
            }, 0);
        });

        resizeObserver.observe(canvas);
        return () => resizeObserver.disconnect();
    }, [content, scaleDxf]);

  return (
    <div>
      <Typography variant="h6" gutterBottom>
        Ansicht Nadelboard Top-Side
      </Typography>
      <canvas
        ref={canvasRef}
        style={{
            width: "100%",
            height: "auto",
            display: "block"
        }}
      />
    </div>
  );
};

export default DxfViewer;
